import React, { useEffect, useState, useCallback } from 'react';
import {
  Navbar,
  Nav,
  Button,
  Offcanvas,
  Collapse,
  Container,
} from 'react-bootstrap';
import { Link, Outlet, useLocation } from 'react-router-dom';
import {
  List as ListIcon,
  Speedometer2,
  People,
  Gear,
  BarChart,
  Folder,
  Kanban,
  ClipboardData,
  CardChecklist,
  FileEarmarkText,
  PersonBadge,
  ShieldLock,
  Tools,
  HouseDoor,
  BoxSeam,
  Truck,
  GeoAlt,
  Flag,
  Layers,
  Bell,
  ChatDots,
  QuestionCircle,
  ChevronRight,
  ChevronDown,
} from 'react-bootstrap-icons';

const STORAGE_KEY = 'sidebar_open_submenu';
const RED = '#dc3545'; // Bootstrap "danger"

function iconForLabel(label = '') {
  const L = label.toLowerCase();
  if (/\bdashboard|home\b/.test(L)) return Speedometer2;
  if (/\buser|team|people\b/.test(L)) return People;
  if (/\bsetting|config|admin\b/.test(L)) return Gear;
  if (/\breport|analytics|chart\b/.test(L)) return BarChart;
  if (/\bproject|board|kanban\b/.test(L)) return Kanban;
  if (/\btask|checklist|todo\b/.test(L)) return CardChecklist;
  if (/\bfile|doc|document\b/.test(L)) return FileEarmarkText;
  if (/\brole|permission|security\b/.test(L)) return ShieldLock;
  if (/\btool|maintenance\b/.test(L)) return Tools;
  if (/\bproduct|inventory|box\b/.test(L)) return BoxSeam;
  if (/\bshipment|deliver|truck\b/.test(L)) return Truck;
  if (/\bmap|geo|location\b/.test(L)) return GeoAlt;
  if (/\bflag|milestone\b/.test(L)) return Flag;
  if (/\blayer|segment\b/.test(L)) return Layers;
  if (/\bnotif|alert|bell\b/.test(L)) return Bell;
  if (/\bchat|message\b/.test(L)) return ChatDots;
  if (/\bhelp|support\b/.test(L)) return QuestionCircle;
  if (/\bfolder|library\b/.test(L)) return Folder;
  if (/\bprofile|id\b/.test(L)) return PersonBadge;
  if (/\bhome\b/.test(L)) return HouseDoor;
  return Folder; // sensible default
}

const ResponsiveSidebar = ({ links = [], brand = 'GVI Morph' }) => {
  const [showMenu, setShowMenu] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState(() => localStorage.getItem(STORAGE_KEY) || null);
  const { pathname } = useLocation();

  const toggleMenu = () => setShowMenu(prev => !prev);

  const toggleSubmenu = useCallback((label) => {
    setOpenSubmenu(prev => {
      const next = prev === label ? null : label;
      try { localStorage.setItem(STORAGE_KEY, next ?? ''); } catch {}
      return next;
    });
  }, []);

  // Auto-open submenu containing the current path
  useEffect(() => {
    const parent = links.find(l => Array.isArray(l.children) && l.children.some(c => pathname.startsWith(c.path)));
    if (parent?.label) {
      setOpenSubmenu(parent.label);
      try { localStorage.setItem(STORAGE_KEY, parent.label); } catch {}
    }
  }, [pathname, links]);

  const isActivePath = useCallback((target) => {
    if (!target) return false;
    return pathname === target || pathname.startsWith(`${target}/`);
  }, [pathname]);


  const LinkIcon = ({ Icon, active }) => (
    <span
      className="d-inline-flex align-items-center justify-content-center rounded-1 flex-shrink-0"
      style={{
        width: 22,
        height: 22,
        background: active ? 'rgba(220,53,69,0.15)' : 'rgba(255,255,255,0.20)', // RED tint when active
      }}
    >
      <Icon size={14} color={active ? '#dc3545' : '#ffffff'} />
    </span>
  );

  const SidebarNav = () => (
    <div className="d-flex flex-column justify-content-between h-100 w-100">
      <Nav className="flex-column pt-4 px-2">
        {links.map(({ label, path, icon, children }) => {
          const Icon = icon || iconForLabel(label);
          const submenuId = `submenu-${label.replace(/\s+/g, '-').toLowerCase()}`;
          const isOpen = openSubmenu === label;

          if (children) {
            const parentActive = children.some(c => isActivePath(c.path));
            return (
              <div key={label} className="px-1 mb-1">
                {/* Parent toggle row */}
                <Button
                  variant="link"
                  onClick={() => toggleSubmenu(label)}
                  className="w-100 text-start d-flex align-items-center gap-2 px-2 py-2 rounded-2 text-white"
                  style={{
                    textDecoration: 'none',
                    background: parentActive ? 'rgba(255,255,255,0.18)' : 'transparent',
                  }}
                  aria-expanded={isOpen}
                  aria-controls={submenuId}
                >
                  <LinkIcon Icon={Icon} active={active} />
                  <span className="flex-grow-1">{label}</span>
                  {isOpen ? <ChevronDown size={16} color="#fff" /> : <ChevronRight size={16} color="#fff" />}
                </Button>

                {/* Submenu */}
                <Collapse in={isOpen}>
                  <div id={submenuId} className="ms-3 mt-1">
                    <Nav className="flex-column">
                      {children.map(({ label: subLabel, path: subPath, icon: SubIcon }) => {
                        const active = isActivePath(subPath);
                        const UsedSubIcon = SubIcon || iconForLabel(subLabel);
                        return (
                          <Nav.Link
                            as={Link}
                            to={subPath}
                            key={subPath}
                            onClick={() => setShowMenu(false)}
                            className={`mb-1 px-2 py-2 rounded-2 d-flex align-items-center gap-2`}
                            style={{
                              color: active ? RED : '#fff',
                              background: active ? '#fff' : 'rgba(255,255,255,0.10)',
                            }}
                          >
                            <LinkIcon Icon={UsedSubIcon} active={active} />
                            <span className={`${active ? 'fw-semibold' : ''}`}>{subLabel}</span>
                          </Nav.Link>
                        );
                      })}
                    </Nav>
                  </div>
                </Collapse>
              </div>
            );
          }

          // Simple top-level link
          const active = isActivePath(path);
          return (
            <Nav.Link
              as={Link}
              to={path}
              key={path}
              onClick={() => setShowMenu(false)}
              className="px-2 mb-2 py-2 rounded-2 d-flex align-items-center gap-2"
              style={{
                color: active ? RED : '#fff',
                background: active ? '#fff' : 'rgba(230, 5, 5, 0)',
                textDecoration: 'none',
              }}
            >
              <LinkIcon Icon={Icon} active={active} />
              <span className={`${active ? 'fw-semibold' : ''}`}>{label}</span>
            </Nav.Link>
          );
        })}
      </Nav>
    </div>
  );

  return (
    <div className="d-flex flex-column flex-md-row" style={{ minHeight: '100dvh' }}>
      {/* Sidebar (Desktop) - solid red background */}
      <div
        className="d-none d-md-flex border-end"
        style={{
          width: 248,
          position: 'sticky',
          top: 0,
          height: '100dvh',
          overflowY: 'auto',
          flexShrink: 0,
          background: '#210e64',
          color: '#fff',
        }}
      >
        <SidebarNav />
      </div>

      {/* Navbar + Offcanvas (Mobile) */}
      <div className="d-md-none w-100">
        <Navbar bg="light" expand={false} className="px-2 border-bottom" style={{ position: 'sticky', top: 0, zIndex: 1040 }}>
          <Container fluid>
            <Button variant="outline-danger" onClick={toggleMenu} aria-label="Open menu">
              <ListIcon />
            </Button>
            <Navbar.Brand className="mx-auto fw-semibold">GVI Morph</Navbar.Brand>
            <div style={{ width: 38 }} /> {/* spacer to balance the left menu button */}
          </Container>
        </Navbar>

        <Offcanvas
          show={showMenu}
          onHide={toggleMenu}
          placement="start"
          style={{ width: 280, background: RED, color: '#fff' }}
        >
          <Offcanvas.Header closeButton closeVariant="white">
            <Offcanvas.Title className="text-white">Menu</Offcanvas.Title>
          </Offcanvas.Header>
          <Offcanvas.Body>
            <SidebarNav />
          </Offcanvas.Body>
        </Offcanvas>
      </div>

      {/* Main Content */}
      <div className="flex-grow-1" style={{ backgroundColor: '#f8f9fa' }}>
        <Outlet />
      </div>
    </div>
  );
};

export default ResponsiveSidebar;
